import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchTournamentsWithPrizeStatusAPI,
  markPrizeAsDeliveredAPI,
  unmarkPrizeAsDeliveredAPI,
} from "./prizeDeliveryAPI";

export const markPrizeAsDelivered = createAsyncThunk(
  "prizeDelivery/markPrizeAsDelivered",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await markPrizeAsDeliveredAPI(payload);
      return { ...payload, delivery: data.delivery };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark prize"
      );
    }
  }
);

export const unmarkPrizeAsDelivered = createAsyncThunk(
  "prizeDelivery/unmarkPrizeAsDelivered",
  async (payload, { rejectWithValue }) => {
    try {
      await unmarkPrizeAsDeliveredAPI(payload);
      return payload;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unmark prize"
      );
    }
  }
);

export const fetchTournamentsWithPrizeStatus = createAsyncThunk(
  "prizeDelivery/fetchTournamentsWithPrizeStatus",
  async ({ page = 1 }, { rejectWithValue }) => {
    try {
      const data = await fetchTournamentsWithPrizeStatusAPI(page, 10);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch tournaments with prize status"
      );
    }
  }
);

const initialState = {
  toggleWinnerStatus: "idle",
  toggleWinnerError: null,

  tournamentsWithPrizeStatus: [],
  fetchWithPrizeStatus: {
    status: "idle",
    error: null,
    page: 1,
    totalPages: 1,
    hasMore: true,
  },
};

const prizeDeliverySlice = createSlice({
  name: "prizeDelivery",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(markPrizeAsDelivered.pending, (state) => {
        state.toggleWinnerStatus = "loading";
        state.toggleWinnerError = null;
      })
      .addCase(markPrizeAsDelivered.fulfilled, (state, action) => {
        const { userId, tableId, tournamentId } = action.payload;

        const tournament = state.tournamentsWithPrizeStatus.find(
          (t) => t._id === tournamentId
        );
        if (tournament) {
          const user = tournament.prizeStatuses.find(
            (entry) => entry.userId === userId && entry.tableId === tableId
          );
          if (user) user.hasReceivedPrize = true;
        }

        state.toggleWinnerStatus = "succeeded";
      })
      .addCase(markPrizeAsDelivered.rejected, (state, action) => {
        state.toggleWinnerStatus = "failed";
        state.toggleWinnerError = action.payload;
      })

      .addCase(unmarkPrizeAsDelivered.pending, (state) => {
        state.toggleWinnerStatus = "loading";
        state.toggleWinnerError = null;
      })
      .addCase(unmarkPrizeAsDelivered.fulfilled, (state, action) => {
        const { userId, tableId, tournamentId } = action.payload;

        const tournament = state.tournamentsWithPrizeStatus.find(
          (t) => t._id === tournamentId
        );
        if (tournament) {
          const user = tournament.prizeStatuses.find(
            (entry) => entry.userId === userId && entry.tableId === tableId
          );
          if (user) user.hasReceivedPrize = false;
        }

        state.toggleWinnerStatus = "succeeded";
      })
      .addCase(unmarkPrizeAsDelivered.rejected, (state, action) => {
        state.toggleWinnerStatus = "failed";
        state.toggleWinnerError = action.payload;
      })

      .addCase(fetchTournamentsWithPrizeStatus.pending, (state) => {
        state.fetchWithPrizeStatus.status = "loading";
        state.fetchWithPrizeStatus.error = null;
      })
      .addCase(fetchTournamentsWithPrizeStatus.fulfilled, (state, action) => {
        const newTournaments = action.payload.data.filter(
          (t) =>
            !state.tournamentsWithPrizeStatus.some(
              (existing) => existing._id === t._id
            )
        );
        state.tournamentsWithPrizeStatus.push(...newTournaments);
        state.fetchWithPrizeStatus.status = "succeeded";
        state.fetchWithPrizeStatus.page = action.payload.page;
        state.fetchWithPrizeStatus.totalPages = action.payload.totalPages;
        state.fetchWithPrizeStatus.hasMore = action.payload.hasMore;
      })
      .addCase(fetchTournamentsWithPrizeStatus.rejected, (state, action) => {
        state.fetchWithPrizeStatus.status = "failed";
        state.fetchWithPrizeStatus.error = action.payload;
      });
  },
});

export default prizeDeliverySlice.reducer;
